import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SocialLoginButtons from '../components/auth/SocialLoginButtons';
import DesktopSignIn from '../desktop/DesktopSignIn';
import { isDesktop } from '../native/desktop';
import MagicLinkForm from '../components/auth/MagicLinkForm';
import { detailToMessage } from '../lib/apiError';
import { isValidEmail } from '../lib/validation';
import MfaChallengeStep from '../components/auth/MfaChallengeStep';
import { PasswordInput } from '../components/ui/PasswordInput';
import { rememberSignInReturn, takeSignInReturn } from '../lib/signInReturn';

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('password');   // 'password' | 'magic'
  const [mfaChallenge, setMfaChallenge] = useState(null);   // string|null
  const { login, finalizeSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Google, Apple and magic links come back on other pages (lib/signInReturn.js).
  const redirect = searchParams.get('redirect');
  useEffect(() => { rememberSignInReturn(redirect); }, [redirect]);
  const redirectAfterLogin = () => {
    navigate(takeSignInReturn(redirect), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // type="email" lets "a@b" through; the backend then 422s with a raw
    // validator message. Trimming also saves the pasted-with-a-space case.
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setError(t('auth.invalid_email'));
      return;
    }

    setLoading(true);

    try {
      const result = await login(trimmedEmail, password);
      if (result?.mfa_required) {
        setMfaChallenge(result.challenge_token);
      } else {
        redirectAfterLogin();
      }
    } catch (err) {
      setError(detailToMessage(err.response?.data?.detail) || t('auth.login_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = async (accessToken) => {
    await finalizeSession(accessToken);
    redirectAfterLogin();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex">
      <SEO title="Log In" description="Log in to your BusyBee.Day account. Access your tasks, projects, calendar, and team workspace." canonical="/login" noindex={true} />
      {/* Left side - Form. flex-col + my-auto so a form taller than the
          viewport scrolls instead of being clipped by centered flex overflow. */}
      <div className="flex-1 flex flex-col items-center px-8 py-12">
        <div className="w-full max-w-md my-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BusyBeeLogo className="h-10" />
          </Link>

          <h1 className="text-3xl font-bold text-white mb-2">{t('auth.welcome_back')}</h1>
          <p className="text-gray-400 mb-8">{t('auth.sign_in_subtitle')}</p>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {mfaChallenge ? (
            <MfaChallengeStep
              challengeToken={mfaChallenge}
              onSuccess={handleMfaSuccess}
              onCancel={() => setMfaChallenge(null)}
            />
          ) : (
          <>
          {isDesktop() ? <DesktopSignIn /> : <SocialLoginButtons mode="login" />}

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-800" />
            <span className="text-xs uppercase tracking-widest text-gray-500">{t('auth.or')}</span>
            <div className="flex-1 h-px bg-gray-800" />
          </div>

          <div className="mb-6 flex gap-1 p-1 bg-zinc-900 rounded-lg">
            <button
              type="button"
              onClick={() => setTab('password')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === 'password' ? 'bg-zinc-800 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('auth.tabs.password')}
            </button>
            <button
              type="button"
              onClick={() => setTab('magic')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
                tab === 'magic' ? 'bg-zinc-800 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('auth.tabs.magicLink')}
            </button>
          </div>

          {tab === 'magic' ? (
            <MagicLinkForm />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-gray-300">{t('auth.email_label')}</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                  placeholder={t('auth.email_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-300">{t('auth.password_label')}</Label>
                <Link to="/forgot-password" className="text-sm text-amber-400 hover:text-amber-300">
                  {t('auth.forgot_password')}
                </Link>
              </div>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                  placeholder={t('auth.password_placeholder')}
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-amber-500 text-black hover:bg-amber-400 h-12"
              disabled={loading}
            >
              {loading ? t('auth.sign_in_action') : t('auth.sign_in')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
          )}
          </>
          )}

          <p className="mt-8 text-center text-gray-400">
            {t('auth.no_account')}{' '}
            <Link
              to={`/register${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}
              className="text-amber-400 hover:text-amber-300"
            >
              {t('auth.sign_up')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-l border-gray-800">
        <div className="text-center px-12">
          <h2 className="text-4xl font-bold text-white mb-4">{t('auth.hero_title')}</h2>
          <p className="text-xl text-gray-400">{t('auth.hero_subtitle')}</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
